import { useEffect } from 'react';
import useScript from './useScript';

const ZendeskSnippetURL = 'https://static.zdassets.com/ekr/snippet.js';
const ZendeskScriptId = 'ze-snippet';

// added actions that will be used.
// extra actions can be found at https://developer.zendesk.com/api-reference/widget/core/#on-userevent
export enum ZendeskUserEventAction {
  WidgetOpened = 'Web Widget Opened',
  WidgetMinimised = 'Web Widget Minimised',
  FormShown = 'Contact Form Shown',
  FormSubmitted = 'Contact Form Submitted',
}
interface ZendeskWidgedOptions {
  themeColor?: string;
  horizontalOffset?: string;
  verticalOffset?: string;
  prefilledFields?: {
    id: string;
    value: string;
  }[];
  onWidgetLoaded?: () => void;
  onUserEvent?: (action: ZendeskUserEventAction) => void;
}

interface ZendeskUserEvent {
  action: ZendeskUserEventAction;
  category: string;
  properties: unknown;
}

export default function useZendeskWidget(zendeskKey: string, options?: ZendeskWidgedOptions): void {
  const zeLoaded = 'zE' in window;
  const onScriptLoaded = () => {
    // callback will be called when the widget is executed
    zE(() => {
      // hide the button
      zE.hide();

      if (options?.onWidgetLoaded) {
        options.onWidgetLoaded();
      }

      // add listener for every user event
      zE('webWidget:on', 'userEvent', (e: ZendeskUserEvent) => {
        if (options?.onUserEvent) {
          options.onUserEvent(e.action);
        }
      });
    });
  };

  useScript(`${ZendeskSnippetURL}?key=${zendeskKey}`, !zeLoaded, ZendeskScriptId, onScriptLoaded);

  useEffect(() => {
    if (!zeLoaded) {
      window.zESettings = {
        webWidget: {
          offset: { horizontal: options?.horizontalOffset || '76px', vertical: options?.verticalOffset || '20px' },
          color: { theme: options?.themeColor || '#2E4D6E' },
        },
        contactForm: {
          fields: options?.prefilledFields?.map((item) => ({ id: item.id, prefill: { '*': item.value } })),
        },
      };
    } else if (options?.onWidgetLoaded) {
      options.onWidgetLoaded();
    }
  }, [zeLoaded, options]);
}

export function updateOptions(options?: ZendeskWidgedOptions): void {
  zE('webWidget', 'updateSettings', {
    webWidget: {
      contactForm: {
        fields: options?.prefilledFields?.map((item) => ({ id: item.id, prefill: { '*': item.value } })),
      },
    },
  });
}
